import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import {FilePreviewCell} from '../file-preview.model';

function sheetColumnLetter(index: number): string {
    const letter = String.fromCharCode(65 + (index % 26));
    return index < 26 ? letter : `${sheetColumnLetter(Math.floor(index / 26) - 1)}${letter}`;
}

@Component({
    selector: 'app-excel-preview',
    standalone: true,
    template: `
        <div class="excel-grid">
            <table class="sheet table m-0">
                <thead>
                    <tr>
                        <td
                            class="sheet-gutter sheet-letter text-center text-secondary user-select-none"
                            aria-hidden="true"></td>
                        @for (letter of columnLetters(); track $index) {
                            <td
                                class="sheet-letter text-center text-secondary user-select-none"
                                aria-hidden="true">
                                {{ letter }}
                            </td>
                        }
                    </tr>
                    @if (header(); as header) {
                        <tr class="sheet-headings">
                            <td
                                class="sheet-gutter text-center text-secondary user-select-none"
                                aria-hidden="true">
                                1
                            </td>
                            @for (cell of header; track $index) {
                                <th scope="col" class="text-nowrap text-primary fw-bold bg-body">
                                    {{ cell }}
                                </th>
                            }
                        </tr>
                    }
                </thead>
                <tbody>
                    @for (row of rows(); track $index) {
                        <tr>
                            <td
                                class="sheet-gutter text-center text-secondary user-select-none"
                                aria-hidden="true">
                                {{ $index + firstDataRowNumber() }}
                            </td>
                            @for (cell of row; track $index) {
                                <td class="text-nowrap" [class.text-end]="cell.numeric">
                                    {{ cell.text }}
                                </td>
                            }
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    `,
    styles: `
        .excel-grid {
            width: max-content;
            min-width: 100%;
        }

        .sheet {
            --bs-table-bg: transparent;
            font-size: 0.8125rem;
            font-variant-numeric: tabular-nums;
        }

        .sheet tr > :is(th, td) {
            height: var(--preview-row-height);
            padding: 0 0.625rem;
            border: 1px solid var(--preview-hairline);
            line-height: var(--preview-row-height);
            vertical-align: middle;
        }

        .sheet tr > :first-child {
            border-left: 0;
        }

        .sheet thead tr:first-child > * {
            border-top: 0;
        }

        .sheet-letter,
        .sheet-gutter {
            background-color: var(--preview-gutter);
            font-size: 0.75rem;
        }

        .sheet-gutter {
            position: sticky;
            left: 0;
            z-index: 1;
            width: var(--preview-gutter-width);
            min-width: var(--preview-gutter-width);
        }

        .sheet-letter {
            position: sticky;
            top: 0;
            z-index: 2;
        }

        .sheet-letter.sheet-gutter {
            z-index: 3;
        }

        .sheet-headings th {
            position: sticky;
            top: var(--preview-row-height);
            z-index: 1;
        }

        .sheet-headings .sheet-gutter {
            top: var(--preview-row-height);
            z-index: 3;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExcelPreviewComponent {
    readonly header = input.required<ReadonlyArray<string> | null>();
    readonly rows = input.required<ReadonlyArray<ReadonlyArray<FilePreviewCell>>>();

    protected readonly columnLetters = computed(() => {
        const header = this.header();
        const columnCount = header === null ? this.rows()[0].length : header.length;
        return Array.from({length: columnCount}, (_, index) => sheetColumnLetter(index));
    });

    protected readonly firstDataRowNumber = computed(() => (this.header() === null ? 1 : 2));
}
